import { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { RuleProvider, useRule } from "../CreateRuleNew/RuleReducer";
import { useDispatch, useSelector } from "react-redux";
import { createAdvRuleApi } from "../../../../../Api Methods/Api";
import WizardStepTrigger from "./WizardStepTrigger";
import WizardStepConditions from "./WizardStepConditions";
import WizardStepRestrictions from "./WizardStepRestrictions";
import WizardStepWhatToDo from "./WizardStepWhatToDo";
import WizardStepActions from "./WizardStepActions";
import WizardStepSnapshotTiming from "./WizardStepSnapshotTiming";
import WizardStepSnapshot from "./WizardStepSnapshot";
import WizardStepAlert from "./WizardStepAlert";
import WizardStepRuleInfo from "./WizardStepRuleInfo";
import classes from "./RuleWizard.module.css";

// Step indices (fixed)
// 0: Trigger
// 1: Conditions / Location
// 2: Restrictions
// 3: What to do?
// 4: Actions          (only if wantOutputs)
// 5: Snapshot timing  (only if wantSnapshot)
// 6: Snapshot cameras (only if wantSnapshot)
// 7: Alert config     (only if wantAlert)
// 8: Rule Info

const RuleWizardInner = ({ allListOptionsArray, firmware, smartLocks, onClose, onSuccess, onFail }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState(new Set());
    const { state, dispatch } = useRule();

    const token = useSelector(s => s.auth.token);
    const client = useSelector(s => s.auth.client);
    const apiDispatch = useDispatch();

    useEffect(() => {
        dispatch({ type: "RESET" });
        dispatch({ type: "SET_SUPPORTS_ADV_RULES", payload: true });
    }, []);

    const goTo = (step) => {
        setCompletedSteps(prev => new Set([...prev, currentStep]));
        setCurrentStep(step);
    };

    const createRule = async (ruleState) => {
        console.log(ruleState);
        const conditionsWithRestrictions = ruleState.conditions.map(cond => {
            const { days_of_week: _omitDaysOfWeek, trigger_method: _omitTriggerMethod, trigger_method_type: _omitTriggerMethodType, ...condWithoutTimeTriggerFields } = cond;
            return {
                ...(ruleState.isTimeTrigger ? condWithoutTimeTriggerFields : cond),
                ...(ruleState.isTimeTrigger ? {} : { days_of_week: JSON.stringify(ruleState.days_of_week.map(Number)) }),
                ...(ruleState.time_window_start && ruleState.time_window_end ? {
                    time_window_start: ruleState.time_window_start,
                    time_window_end: ruleState.time_window_end,
                    time_condition: "DURING",
                } : {}),
            };
        });

        const triggerFields = ruleState.isTimeTrigger ? {
            type: "TIME",
            local_time: ruleState.triggerTime,
            days_of_week: ruleState.days_of_week.map(Number),
            timezone: ruleState.triggerTimezone,
        } : null;

        const body = ruleState.isGeofence
            ? {
                site_owner: String(ruleState.deviceUUID),
                name: String(ruleState.name),
                description: String(ruleState.description),
                conditions_logic_operator: "AND",
                is_enabled: true,
                conditions: conditionsWithRestrictions,
                actions: ruleState.actions,
                ...(triggerFields ? triggerFields : {}),
            }
            : {
                site_owner: String(ruleState.deviceUUID),
                name: String(ruleState.name),
                description: String(ruleState.description),
                evaluation_mode: ruleState.evaluation_mode,
                conditions_logic_operator: String(ruleState.conditions_logic_operator),
                is_enabled: true,
                conditions: conditionsWithRestrictions,
                actions: ruleState.actions,
                ...(ruleState.timeframe_window_ms ? (ruleState.evaluation_mode === "SEQUENCE" ? { sequence_timeout_ms: ruleState.timeframe_window_ms } : { timeframe_window_ms: ruleState.timeframe_window_ms }) : {}),
                ...(triggerFields ? { trigger: triggerFields } : {}),
            };
console.log(body);
        const response = await createAdvRuleApi({ token, client }, body, apiDispatch);
        console.log(response);
        if (response.success) {
            onClose();
            onSuccess();
        } else {
            onFail();
        }
    };

    const DAY_NAMES = ["Sun", "Mon", "Tues", "Wed", "Thur", "Fri", "Sat"];

    const convertDayLabels = (days) => {
        const sorted = [...days].map(Number).sort((a, b) => a - b);

        if (sorted.length === 7) return "everyday";

        const isWeekday = (d) => d >= 1 && d <= 5;
        const isWeekend = (d) => d === 0 || d === 6;

        if (sorted.length === 5 && sorted.every(isWeekday)) return "weekdays";
        if (sorted.length === 2 && sorted.every(isWeekend)) return "weekends";

        return sorted.map(d => DAY_NAMES[d]).join(", ");
    }

    // Dynamic sidebar — only show steps that apply given current selections
    const getSidebarConditionLabel = (cond) => {
        const prefix = cond.is_absence_condition ? "NOT " : "";
        const still = state.isTimeTrigger ? "still " : "";
        const onLabel  = state.isLock ? "closed" : (state.pinStatusOn  || "armed");
        const offLabel = state.isLock ? "open"   : (state.pinStatusOff || "disarmed");
    
        const stateStr = {
            ARMED:    `${still}${onLabel}`,
            DISARMED: `${still}${offLabel}`,
            ANY:      `${onLabel} or ${offLabel}`,
            PULSE:    "pulsed",
        }[cond.pin_state] ?? (cond.pin_state || "...");
        const timeSuffix = state.isTimeTrigger && state.triggerTime ? ` at ${state.triggerTime.slice(0, 5)}` : "";
        return `${cond.pin_name} is ${prefix}${stateStr}${timeSuffix}`;
    };

    const formatMsDuration = (ms) => {
        if (ms < 1000) return `${ms}ms`;
        const seconds = ms / 1000;
        if (seconds <= 60) return `${seconds} seconds`;
        const minutes = seconds / 60;
        return `${minutes} minutes`;
    };

    const getTimeframeBetweenConditionsLabel = (cond) => {
        console.log(cond); //here, we dont have condition since there could be multiple - this pill is to render the restrictions once rather than per condition
        const timeRestriction = state.time_window_start_local != null && state.time_window_end_local != null ? `between ${state.time_window_start_local} and ${state.time_window_end_local}` : ""
        const daysRestriction = convertDayLabels(state.days_of_week)
        const timeframeRestriction = state.timeframe_window_ms != null ? `within ${formatMsDuration(state.timeframe_window_ms)} of eachother` : ""
        return `${timeRestriction} ${daysRestriction} ${timeframeRestriction}`
    }

    const SNAPSHOT_PROVIDER_LABELS = { ajax: "Ajax", dahua: "Dahua", hikvision: "Hikvision" };

    const getSidebarSnapshotLabel = (camera) => {
        const name = camera.name ?? camera.cameraName ?? "Camera";
        const providerLabel = SNAPSHOT_PROVIDER_LABELS[camera.type] ?? camera.type;
        const channelText = camera.channel != null ? ` (Channel ${camera.channel})` : "";
        return providerLabel ? `${providerLabel} - ${name}${channelText}` : `${name}${channelText}`;
    };

    const getSidebarActionLabel = (pin) => {
        const action = state.actions.find(
            a => a.site_to === pin.DeviceUUID && a.target_output === String(pin.Pin)
        );
        console.log(action);
        const delayText = action.delay_seconds != 0 ? `after ${action.delay_seconds} seconds` : ""
        const typeLabels = {
            ARM: "Arm", TEMP_ARM: "Temp. Arm", DISARM: "Disarm",
            TEMP_DISARM: "Temp. Disarm", TOGGLE: "Toggle",
            PULSE: "Pulse", MATCH: "Match", OPPOSE: "Oppose",
        };
        const verb = action?.control_type ? (typeLabels[action.control_type] ?? action.control_type) : "";
        return verb ? `${pin.pinName} will ${verb} ${delayText}` : pin.pinName;
    };

    const getStepSummary = (step) => {
        switch (step) {
            case 0: {
                if (state.isGeofence) return `Geofence · ${state.geofenceDirection}`;
                if (state.isTimeTrigger) return state.triggerTime ? `Time · ${state.triggerTime.slice(0, 5)}` : "Time";
                return state.selectedRulePin?.pinName || state.pinName || null;
            }
            case 1: {
                if (state.isGeofence) return `${state.geofenceDirection} zone`;
                const count = state.conditions.length;
                return count > 0 ? `${count} condition${count !== 1 ? "s" : ""}` : null;
            }
            case 2: {
                const dayCount = state.days_of_week.length;
                const hasTime = state.time_window_start && state.time_window_end;
                if (dayCount === 7 && !hasTime) return "Any time";
                const parts = [];
                if (dayCount < 7) parts.push(`${dayCount} day${dayCount !== 1 ? "s" : ""}`);
                if (hasTime) parts.push(`${state.time_window_start_local}–${state.time_window_end_local}`);
                return parts.join(" · ") || null;
            }
            case 3: {
                const parts = [];
                if (state.wantOutputs) parts.push("Outputs");
                if (state.wantAlert) parts.push("Alert");
                if (state.wantSnapshot) parts.push("Snapshot");
                if (state.wantArcAlert) parts.push("ARC");
                return parts.join(" + ") || null;
            }
            case 4: {
                const pins = state.selectedPinsToAffect;
                if (pins.length === 0) return null;
                if (pins.length <= 2) return pins.map(p => p.pinName).join(", ");
                return `${pins.length} outputs`;
            }
            case 5: {
                const mode = state.snapshotTimingMode;
                if (mode === "BOTH") return `Immediate + ${state.snapshotDelaySeconds}s`;
                if (mode === "DELAY") return `After ${state.snapshotDelaySeconds}s`;
                return "Immediately";
            }
            case 6: {
                const cams = state.selectedSnapshotCameras;
                if (cams.length === 0) return null;
                if (cams.length === 1) return getSidebarSnapshotLabel(cams[0]);
                return `${cams.length} cameras`;
            }
            case 7: {
                return state.target_users ? "Recipients set" : null;
            }
            default:
                return null;
        }
    };

    const stepConfigs = [
        { step: 0, label: "Trigger" },
        { step: 1, label: state.isGeofence ? "Location" : "Conditions" },
        ...(state.isTimeTrigger ? [] : [{ step: 2, label: "Restrictions" }]),
        { step: 3, label: "What to do?" },
        ...(state.wantOutputs  ? [{ step: 4, label: "Actions" }]  : []),
        ...(state.wantSnapshot ? [{ step: 5, label: "Snapshot timing" }] : []),
        ...(state.wantSnapshot ? [{ step: 6, label: "Snapshot cameras" }] : []),
        ...(state.wantAlert    ? [{ step: 7, label: "Alert" }]    : []),
        { step: 8, label: "Rule Info" },
    ];

    const renderStep = () => {
        switch (currentStep) {
            case 0:
                return (
                    <WizardStepTrigger
                        pins={allListOptionsArray}
                        firmware={firmware}
                        smartLocks={smartLocks}
                        onNext={() => goTo(1)}
                    />
                );
            case 1:
                return (
                    <WizardStepConditions
                        pins={allListOptionsArray}
                        smartLocks={smartLocks}
                        onNext={() => goTo(state.isTimeTrigger ? 3 : 2)}
                        onBack={() => goTo(0)}
                    />
                );
            case 2:
                return (
                    <WizardStepRestrictions
                        onNext={() => goTo(3)}
                        onBack={() => goTo(1)}
                    />
                );
            case 3:
                return (
                    <WizardStepWhatToDo
                        onNext={() => goTo(state.wantOutputs ? 4 : state.wantSnapshot ? 5 : state.wantAlert ? 7 : 8)}
                        onBack={() => goTo(state.isTimeTrigger ? 1 : 2)}
                    />
                );
            case 4:
                return (
                    <WizardStepActions
                        pins={allListOptionsArray}
                        smartLocks={smartLocks}
                        onNext={() => goTo(state.wantSnapshot ? 5 : state.wantAlert ? 7 : 8)}
                        onBack={() => goTo(3)}
                    />
                );
            case 5:
                return (
                    <WizardStepSnapshotTiming
                        onNext={() => goTo(6)}
                        onBack={() => goTo(state.wantOutputs ? 4 : 3)}
                    />
                );
            case 6:
                return (
                    <WizardStepSnapshot
                        onNext={() => goTo(state.wantAlert ? 7 : 8)}
                        onBack={() => goTo(5)}
                    />
                );
            case 7:
                return (
                    <WizardStepAlert
                        onNext={() => goTo(8)}
                        onBack={() => goTo(state.wantSnapshot ? 6 : state.wantOutputs ? 4 : 3)}
                    />
                );
            case 8:
                return (
                    <WizardStepRuleInfo
                        onBack={() => goTo(state.wantAlert ? 7 : state.wantSnapshot ? 6 : state.wantOutputs ? 4 : 3)}
                        onCreate={createRule}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className={classes.wizardContainer}>
            <div className={classes.sidebar}>
                <span className={classes.sidebarTitle}>Create Rule</span>
                {stepConfigs.map((config, i) => {
                    const isActive = config.step === currentStep;
                    const isDone = completedSteps.has(config.step) && !isActive;
                    const summary = isDone ? getStepSummary(config.step) : null;
                    return (
                        <div
                            key={config.step}
                            className={`${classes.stepItem} ${isActive ? classes.stepItemActive : ""}`}
                        >
                            <div
                                className={`${classes.stepNumber} ${
                                    isActive ? classes.stepNumberActive : isDone ? classes.stepNumberDone : ""
                                }`}
                            >
                                {isDone ? "✓" : i + 1}
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                                <span
                                    className={`${classes.stepLabel} ${
                                        isActive ? classes.stepLabelActive : isDone ? classes.stepLabelDone : ""
                                    }`}
                                >
                                    {config.label}
                                </span>
                                {summary && (
                                    <span className={classes.stepSummary}>{summary}</span>
                                )}
                            </div>
                        </div>
                    );
                })}
                {(() => {
                    const hasActions = state.isTimeTrigger || state.selectedPinsToAffect.length > 0 || state.wantAlert || state.wantSnapshot || state.wantArcAlert;
                    if (!state.conditions.length && !hasActions) return null;
                    return (
                        <div className={classes.sidebarSummary}>
                            {state.conditions.length > 0 && (
                                <div className={classes.sidebarSummarySection}>
                                    <span className={classes.sidebarSummaryLabel}>Conditions</span>
                                    {state.conditions.map((cond, i) => (
                                        <span key={i} style={{ display: "contents" }}>
                                            <span className={classes.sidebarChip} title={getSidebarConditionLabel(cond)}>
                                                {getSidebarConditionLabel(cond)}
                                            </span>
                                            {state.evaluation_mode === "SEQUENCE" && i < state.conditions.length - 1 && (
                                                <span className={classes.sidebarChipThen}>THEN</span>
                                            )}
                                        </span>
                                    ))}
                                        {<span key={0} className={classes.sidebarChip}>
                                             {getTimeframeBetweenConditionsLabel(state.conditions)}
                                        </span>}
                                </div>
                            )}
                            {hasActions && (
                                <div className={classes.sidebarSummarySection}>
                                    <span className={classes.sidebarSummaryLabel}>Actions</span>
                                    {state.isTimeTrigger && (
                                        <span className={classes.sidebarChipAction} title={`Triggers at ${state.triggerTime?.slice(0, 5)}`}>
                                            {`Triggers at ${state.triggerTime?.slice(0, 5) ?? "..."}`}
                                        </span>
                                    )}
                                    {state.selectedPinsToAffect.map((pin, i) => (
                                        <span key={i} className={classes.sidebarChipAction} title={getSidebarActionLabel(pin)}>
                                            {getSidebarActionLabel(pin)}
                                        </span>
                                    ))}
                                    {state.wantAlert    && <span className={classes.sidebarChipAction}>Send alert</span>}
                                    {state.wantSnapshot && (
                                        state.selectedSnapshotCameras.length > 0
                                            ? state.selectedSnapshotCameras.map((cam, i) => (
                                                <span key={`snapshot-${i}`} className={classes.sidebarChipAction} title={getSidebarSnapshotLabel(cam)}>
                                                    {getSidebarSnapshotLabel(cam)}
                                                </span>
                                            ))
                                            : <span className={classes.sidebarChipAction}>Take snapshot</span>
                                    )}
                                    {state.wantArcAlert && <span className={classes.sidebarChipAction}>ARC alert</span>}
                                </div>
                            )}
                        </div>
                    );
                })()}
            </div>
            <div className={classes.content}>
                {renderStep()}
            </div>
        </div>
    );
};

const RuleWizard = ({
    allListOptionsArray,
    firmware,
    smartLocks,
    setShowAutomation,
    setSuccessVisible,
    setFailVisible,
    fetchAutomations,
}) => {
    const handleClose = () => setShowAutomation(null);
    const handleSuccess = () => {
        setSuccessVisible(true);
        if (fetchAutomations) fetchAutomations();
    };
    const handleFail = () => setFailVisible(true);

    return (
        <RuleProvider>
            <Dialog
                visible
                onHide={handleClose}
                style={{ width: "900px" }}
                header="Create Rule"
                modal
                closable
                pt={{ content: { style: { padding: 0 } } }}
            >
                <RuleWizardInner
                    allListOptionsArray={allListOptionsArray}
                    firmware={firmware}
                    smartLocks={smartLocks}
                    onClose={handleClose}
                    onSuccess={handleSuccess}
                    onFail={handleFail}
                />
            </Dialog>
        </RuleProvider>
    );
};

export default RuleWizard;
